import { FieldPolicy, FieldFunctionOptions } from "@apollo/client";
import { SearchQuery, SortDir, Task, TaskSortFields } from "@graphql/types";

type ReadFieldFunction = FieldFunctionOptions["readField"];

const getSortField = (
    sortCol: TaskSortFields,
    value: Partial<Task>,
    readField: ReadFieldFunction,
) => {
  switch (sortCol) {
    case TaskSortFields.Tasktype:
      return readField("typeName", value);
    case TaskSortFields.Name:
      return readField("name", value);
    case TaskSortFields.Createdat:
      return readField("createdAt", value);
    case TaskSortFields.Updatedat:
    default:
      return readField("updatedAt", value) ?? readField("createdAt", value);
  }
};

// list of all filters without cursor
// if add cursor then fetchMore will create a new cache slot
const SEARCH_FILTERS_KEY_ARGS = [
  "perPage",
  "sortCol",
  "sortDir",
  "projectId",
  "q",
  "taskStatuses",
  "taskStages",
  "taskTypes",
  "taskActiveAt",
] as const;

export const tasks: FieldPolicy<SearchQuery["search"]> = {
  keyArgs: [
    "scope",
    ["filters", [...SEARCH_FILTERS_KEY_ARGS]],
  ],

  merge(existing, incoming, { args, readField }) {
    const safeExisting = existing ?? { hasMore: true, cursor: null, results: [] as any[] };
    const cursor = (args as any)?.filters?.cursor;

    const mergedRaw = cursor
        ? [...(safeExisting.results ?? []), ...(incoming?.results ?? [])]
        : [...(incoming?.results ?? [])];

    const seen = new Set<string>();
    const unique = mergedRaw.filter((row) => {
      const taskRef = row?.task;
      const id = taskRef ? (readField("id", taskRef) as string) : undefined;
      if (!id) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    const sortDir = (args as any)?.filters?.sortDir ?? SortDir.Desc;
    const sortCol = (args as any)?.filters?.sortCol ?? TaskSortFields.Updatedat;
    const sortIndicator = sortDir === SortDir.Desc ? -1 : 1;

    // Cache sort values to avoid expensive readField calls inside the sort loop
    const sortValues = new Map<any, any>();
    unique.forEach((row) => {
      sortValues.set(row, getSortField(sortCol, row.task, readField));
    });

    unique.sort((a, b) => {
      const aValue = sortValues.get(a);
      const bValue = sortValues.get(b);
      if (aValue < bValue) return -1 * sortIndicator;
      if (aValue > bValue) return 1 * sortIndicator;
      return 0;
    });

    return {
      ...safeExisting,
      ...incoming,
      cursor: incoming?.cursor ?? null,
      hasMore: incoming?.hasMore ?? true,
      results: unique,
    };
  },
};
